import { Box, Circle, Divider, Flex, Image, Text } from '@chakra-ui/react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { CustomNfcIcon } from '../../../../../theme/icons/CustomNfcIcon';
import { MeContext } from '../../../../../context/MeContext';
import previewWatermarkLight from '../../../../../assets/vectors/previewWatermarkLight.svg';
import previewWatermarkDark from '../../../../../assets/vectors/previewWatermarkDark.svg';
import { CustomMeatballIcon } from '../../../../../theme/icons/CustomMeatballIcon';
import { PreviewData, TemplateType } from '../../../../../store/types/template';
import { PassNotification } from '../shared/PassNotification';
import { ImagePlaceholder } from '../shared/ImagePlaceholder';
import { TrialBannerOverlay } from '../shared/TrialBannerOverlay';
import BarcodePreview from '../BarcodePreview';
import {
  getPassDisplayConfig,
  getPreviewBarcodeDimensions,
  getPreviewBarcodeImage,
  getPreviewTransitIcon,
  getAppleWatermarkConfig,
  getApplePreviewNotificationSubtitle,
} from '../../utils/applePassPreview';
import { ApplePassField } from './ApplePassField';
import { getImageDimensions } from '../../utils/imageDimensions';

const ApplePassFront = ({
  previewData,
  createTemplate,
  templatesPage,
  notifOpen,
  cutoutBg,
}: {
  previewData: PreviewData;
  createTemplate?: boolean;
  templatesPage?: boolean;
  notifOpen: boolean;
  cutoutBg?: string;
}) => {
  const meCtx = useContext(MeContext);
  const watermarkEnabled = (meCtx as any).state.watermarkEnabled;
  const isInTrial = (meCtx as any).state.organisationStatus === 'trial';

  const isWatermarkFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('design.shared.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.apple.watermarkColor') ||
      previewData.isFieldHighlighted?.('design.shared.watermarkPosition') ||
      previewData.isFieldHighlighted?.('design.apple.watermarkPosition')
    );
  }, [previewData.isFieldHighlighted]);

  const isBarcodeFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('barcode.enabled') ||
      previewData.isFieldHighlighted?.('barcode.type') ||
      previewData.isFieldHighlighted?.('barcode.source') ||
      previewData.isFieldHighlighted?.('barcode.value')
    );
  }, [previewData.isFieldHighlighted]);

  const isNfcFieldHighlighted = useMemo(() => {
    return (
      previewData.isFieldHighlighted?.('nfc.enabled') ||
      previewData.isFieldHighlighted?.('nfc.source') ||
      previewData.isFieldHighlighted?.('nfc.customValue')
    );
  }, [previewData.isFieldHighlighted]);

  const [previewBarcodeImage, setPreviewBarcodeImage] = useState<string | null>(
    null
  );
  const [isBannerHovered, setIsBannerHovered] = useState(false);

  const {
    thumbnailDisplay,
    bannerDisplay,
    primaryDisplay,
    labelDisplay,
    borderRadius,
  } = useMemo(
    () => getPassDisplayConfig(previewData.passType),
    [previewData.passType]
  );

  const { width: previewBarcodeImageWidth, height: previewBarcodeImageHeight } =
    useMemo(
      () => getPreviewBarcodeDimensions(previewData?.barcode?.type),
      [previewData?.barcode?.type]
    );

  const appleBannerAspectRatio = useMemo(() => {
    const { width, height } = getImageDimensions(
      previewData.passType as TemplateType
    ).apple.banner;
    return String(width / height);
  }, [previewData.passType]);

  useEffect(() => {
    const img = getPreviewBarcodeImage(previewData?.barcode?.type);
    setPreviewBarcodeImage(img);
  }, [previewData?.barcode?.type]);

  const { color, position } = useMemo(
    () => getAppleWatermarkConfig(previewData),
    [previewData]
  );

  return (
    <>
      {/* MENU ICON */}
      {!templatesPage && (
        <Box
          position="absolute"
          inset="13% 8% auto 10%"
          display="flex"
          justifyContent="end"
          pr="10px"
        >
          <Circle size="21px" border="2px" p={1}>
            <CustomMeatballIcon boxSize="14px" />
          </Circle>
        </Box>
      )}

      {/* NOTIFICATION */}
      {(() => {
        const isIconFocused =
          previewData.focusedField === 'design.shared.icon' ||
          previewData.focusedField === 'design.apple.icon';
        const iconImageUrl = previewData.appleIconLogo || previewData.iconLogo;
        return (
          <PassNotification
            open={notifOpen || isIconFocused}
            autoHideMs={0}
            title={previewData?.notificationHeader || 'Notification header'}
            subtitle={getApplePreviewNotificationSubtitle(previewData)}
            imageUrl={iconImageUrl}
            inset="12.5% 7.5% auto 7%"
            device="apple"
            highlightIcon={isIconFocused}
          />
        );
      })()}

      {/* PASS CONTENT */}
      <Box
        position={templatesPage ? 'static' : 'absolute'}
        inset="18% 8% auto 7%"
        minH={templatesPage ? '350px' : '300px'}
        bg={previewData.passBackgroundColor}
        borderRadius={borderRadius}
        border="1px solid #E0E0E6"
        boxShadow={
          templatesPage ? 'none' : '0 8px 6px -4px rgba(0, 0, 0, 0.25)'
        }
        py={2}
      >
        {/* EVENT TICKET CUTOUT */}
        <Box
          display={previewData.passType === 'eventTicket' ? 'block' : 'none'}
          w={templatesPage ? '100px' : '70px'}
          h={templatesPage ? '21px' : '16px'}
          pos="absolute"
          left="50%"
          transform="translateX(-50%)"
          top={templatesPage ? '-1px' : '-2px'}
          pointerEvents="none"
          overflow="hidden"
          zIndex={2}
        >
          <Circle
            display={previewData.passType === 'eventTicket' ? 'block' : 'none'}
            bg={cutoutBg ?? (templatesPage ? '#F8F8F8' : '#F3F2FA')}
            size={templatesPage ? '100px' : '70px'}
            pos="absolute"
            bottom="0"
            pointerEvents="none"
            border="1px solid #E0E0E6"
          />
        </Box>

        {/* PASS HEADER CONTENT */}
        <Flex
          justifyContent="space-between"
          pt="5px"
          px="18px"
          alignContent="center"
          position="relative"
        >
          {/* LOGO */}
          <Flex w="156px" h="40px" alignItems="center" borderRadius="5px">
            {(() => {
              const logoImage =
                previewData.appleLogoImage || previewData.logoImage;

              const isHighlighted =
                previewData.isFieldHighlighted?.('design.shared.logo') ||
                previewData.isFieldHighlighted?.('design.apple.logo');

              if (logoImage) {
                return (
                  <Image
                    maxH="full"
                    width="calc(100% - 35px)"
                    mt="5px"
                    minW="100px"
                    src={logoImage}
                    alt=""
                    boxShadow={isHighlighted ? '0 0 0 2px #FF4935' : 'none'}
                    borderRadius="4px"
                  />
                );
              } else {
                return (
                  <ImagePlaceholder
                    height="40px"
                    width="100px"
                    boxShadow={isHighlighted ? '0 0 0 2px #FF4935' : 'none'}
                    borderRadius="4px"
                  />
                );
              }
            })()}
          </Flex>

          {/* HEADER ONE */}
          {(previewData.headerOneId ||
            previewData.headerOneLabel ||
            previewData.headerOneValue ||
            previewData.isFieldHighlighted?.('header.one.id') ||
            previewData.isFieldHighlighted?.('header.one.label') ||
            previewData.isFieldHighlighted?.('header.one.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              w="fit-content"
              h="fit-content"
              minWidth="120px"
              ml={2}
              textAlign="right"
              label={previewData.headerOneLabel}
              id={previewData.headerOneId}
              value={previewData.headerOneValue}
              align="right"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('loyaltyBalance.enabled') ||
                previewData.isFieldHighlighted?.('loyaltyBalance.numberType') ||
                previewData.isFieldHighlighted?.('header.one.id') ||
                previewData.isFieldHighlighted?.('header.one.label') ||
                previewData.isFieldHighlighted?.('header.one.defaultValue')
              }
            />
          )}
        </Flex>

        {/* BANNER */}
        <Box
          h="100%"
          width="100%"
          mt="10px"
          mb={1}
          display={bannerDisplay}
          position="relative"
          onMouseEnter={() => setIsBannerHovered(true)}
          onMouseLeave={() => setIsBannerHovered(false)}
        >
          {(() => {
            const bannerUrl =
              previewData.passDesign?.bannerImageUrl ||
              previewData.appleBannerImage ||
              previewData.bannerImage;

            const isHighlighted =
              previewData.isFieldHighlighted?.('design.shared.banner') ||
              previewData.isFieldHighlighted?.('design.apple.banner') ||
              previewData.isStampBannerHighlighted;

            if (!bannerUrl) {
              return (
                <ImagePlaceholder
                  width="100%"
                  height="100%"
                  aspectRatio={appleBannerAspectRatio}
                  boxShadow={isHighlighted ? '0 0 0 2px #FF4935' : 'none'}
                />
              );
            }
            return (
              <>
                <Image
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  src={bannerUrl}
                  alt=""
                  aspectRatio={appleBannerAspectRatio}
                  boxShadow={isHighlighted ? '0 0 0 2px #FF4935' : 'none'}
                />
                {!isBannerHovered && (
                  <TrialBannerOverlay isInTrial={isInTrial} />
                )}
                {/* WATERMARK */}
                {watermarkEnabled &&
                  previewData.displayBannerWatermarkPreview && (
                    <Box
                      position="absolute"
                      top={
                        (position === 'topLeft' || position === 'topRight') &&
                        '0'
                      }
                      bottom={
                        (position === 'bottomLeft' ||
                          position === 'bottomRight') &&
                        '0'
                      }
                      left={
                        (position === 'topLeft' || position === 'bottomLeft') &&
                        '0'
                      }
                      right={
                        (position === 'topRight' ||
                          position === 'bottomRight') &&
                        '0'
                      }
                      mx="9px"
                      my="5px"
                      borderRadius="4px"
                      border={
                        isWatermarkFieldHighlighted
                          ? '2px solid #FF4935'
                          : 'none'
                      }
                      p={isWatermarkFieldHighlighted ? '4px' : '0'}
                    >
                      <Image
                        src={
                          color === 'light'
                            ? previewWatermarkLight
                            : previewWatermarkDark
                        }
                        alt="watermark"
                        w="112px"
                        h="18.5px"
                      />
                    </Box>
                  )}
              </>
            );
          })()}
        </Box>

        {/* TRANSIT CUTOUTS */}
        {previewData.passType === 'transit' && (
          <>
            <Box
              display="block"
              w="10px"
              h="10px"
              pos="absolute"
              top={templatesPage ? '115px' : '105px'}
              left={templatesPage ? '5px' : '4px'}
              transform="translateX(-50%)"
              pointerEvents="none"
              overflow="hidden"
              zIndex={2}
            >
              <Circle
                display="block"
                bg={templatesPage ? '#F3F3F3' : '#EEEDF7'}
                size="10px"
                pos="absolute"
                pointerEvents="none"
                border="1px solid #E0E0E6"
                left="-5px"
              />
            </Box>
            <Box
              display="block"
              w="10px"
              h="10px"
              pos="absolute"
              top={templatesPage ? '115px' : '105px'}
              right={templatesPage ? '5px' : '4px'}
              transform="translateX(50%)"
              pointerEvents="none"
              overflow="hidden"
              zIndex={2}
            >
              <Circle
                display="block"
                bg={templatesPage ? '#F3F3F3' : '#EEEDF7'}
                size="10px"
                pos="absolute"
                pointerEvents="none"
                border="1px solid #E0E0E6"
                right="-5px"
              />
            </Box>
          </>
        )}

        {/* PRIMARY ONE AND THUMBNAIL (GENERIC) */}
        {previewData.passType === 'generic' && (
          <Flex
            justifyContent="space-between"
            px="16px"
            mb={1}
            mt="10px"
            minH="80px"
            alignItems="flex-start"
          >
            {/* PRIMARY ONE */}
            {(previewData.primaryOneId ||
              previewData.primaryOneLabel ||
              previewData.primaryOneValue ||
              previewData.isFieldHighlighted?.('central.one.id') ||
              previewData.isFieldHighlighted?.('central.one.label') ||
              previewData.isFieldHighlighted?.('central.one.defaultValue')) && (
              <Box
                mt="10px"
                mb={1}
                display={primaryDisplay}
                flex="1"
                minW={0}
                mr={previewData.thumbnailImage ? 2 : 0}
              >
                <ApplePassField
                  hideIdPlaceholder={previewData.hideIdPlaceholder}
                  width="100%"
                  minWidth="0"
                  textAlign="left"
                  label={previewData.primaryOneLabel}
                  id={previewData.primaryOneId}
                  value={previewData.primaryOneValue}
                  align="left"
                  titleColor={previewData.titleFontColor}
                  bodyColor={previewData.bodyFontColor}
                  minFontSize={10}
                  maxFontSize={20}
                  columnHeight="40px"
                  valueHeight="24px"
                  isHighlighted={
                    previewData.isFieldHighlighted?.('central.one.id') ||
                    previewData.isFieldHighlighted?.('central.one.label') ||
                    previewData.isFieldHighlighted?.('central.one.defaultValue')
                  }
                />
              </Box>
            )}

            {/* THUMBNAIL */}
            {(!!previewData.thumbnailImage ||
              !!previewData.passDesign?.thumbnailImageUrl ||
              previewData.isFieldHighlighted?.('design.apple.thumbnail')) && (
              <Box
                h="90px"
                width="90px"
                flexShrink={0}
                mt="10px"
                mb={1}
                ml="auto"
                display={thumbnailDisplay}
              >
                {!!previewData.thumbnailImage ||
                !!previewData.passDesign?.thumbnailImageUrl ? (
                  <Image
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    src={
                      !!previewData.passDesign &&
                      !!previewData.passDesign.thumbnailImageUrl
                        ? previewData.passDesign.thumbnailImageUrl
                        : previewData.thumbnailImage
                    }
                    alt=""
                    boxShadow={
                      previewData.isFieldHighlighted?.('design.apple.thumbnail')
                        ? '0 0 0 2px #FF4935'
                        : 'none'
                    }
                    borderRadius="4px"
                  />
                ) : (
                  <ImagePlaceholder
                    width="100%"
                    height="100%"
                    borderRadius="4px"
                    boxShadow={
                      previewData.isFieldHighlighted?.('design.apple.thumbnail')
                        ? '0 0 0 2px #FF4935'
                        : 'none'
                    }
                  />
                )}
              </Box>
            )}
          </Flex>
        )}

        {/* TRANSIT CONTENT */}
        {previewData.passType === 'transit' && (
          <>
            <Flex
              justifyContent="space-between"
              px="12px"
              mb={1}
              position="relative"
              mt="10px"
              alignContent="center"
            >
              {/* PRIMARY ONE */}
              {(previewData.primaryOneId ||
                previewData.primaryOneLabel ||
                previewData.primaryOneValue ||
                previewData.isFieldHighlighted?.('central.one.id') ||
                previewData.isFieldHighlighted?.('central.one.label') ||
                previewData.isFieldHighlighted?.(
                  'central.one.defaultValue'
                )) && (
                <Box mb={1} display={primaryDisplay} maxW="40%">
                  <ApplePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    width="100%"
                    minWidth="40px"
                    textAlign="left"
                    label={previewData.primaryOneLabel}
                    id={previewData.primaryOneId}
                    value={previewData.primaryOneValue}
                    align="left"
                    titleColor={previewData.titleFontColor}
                    bodyColor={previewData.bodyFontColor}
                    minFontSize={10}
                    maxFontSize={20}
                    columnHeight="40px"
                    valueHeight="24px"
                    isHighlighted={
                      previewData.isFieldHighlighted?.('central.one.id') ||
                      previewData.isFieldHighlighted?.('central.one.label') ||
                      previewData.isFieldHighlighted?.(
                        'central.one.defaultValue'
                      )
                    }
                  />
                </Box>
              )}

              {/* TRANSIT ICON */}
              <Box
                position="absolute"
                left="50%"
                top="55%"
                transform="translate(-50%, -50%)"
                pointerEvents="none"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={2}
              >
                {getPreviewTransitIcon(
                  previewData.transitType,
                  previewData.titleFontColor,
                  templatesPage
                )}
              </Box>

              {/* PRIMARY TWO */}
              {(previewData.primaryTwoId ||
                previewData.primaryTwoLabel ||
                previewData.primaryTwoValue ||
                previewData.isFieldHighlighted?.('central.two.id') ||
                previewData.isFieldHighlighted?.('central.two.label') ||
                previewData.isFieldHighlighted?.(
                  'central.two.defaultValue'
                )) && (
                <Box mb={1} display={primaryDisplay} maxW="40%">
                  <ApplePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    width="100%"
                    minWidth="40px"
                    textAlign="right"
                    label={previewData.primaryTwoLabel}
                    id={previewData.primaryTwoId}
                    value={previewData.primaryTwoValue}
                    align="right"
                    titleColor={previewData.titleFontColor}
                    bodyColor={previewData.bodyFontColor}
                    minFontSize={10}
                    maxFontSize={20}
                    columnHeight="40px"
                    valueHeight="24px"
                    isHighlighted={
                      previewData.isFieldHighlighted?.('central.two.id') ||
                      previewData.isFieldHighlighted?.('central.two.label') ||
                      previewData.isFieldHighlighted?.(
                        'central.two.defaultValue'
                      )
                    }
                  />
                </Box>
              )}
            </Flex>
          </>
        )}

        {/* DETAILS FIELDS */}
        <Flex
          justifyContent="space-between"
          mt={
            templatesPage && previewData.passType === 'transit'
              ? '25px'
              : '15px'
          }
          px="12px"
          position="relative"
          columnGap={2}
        >
          {/* DETAILS ONE */}
          {(previewData.detailsOneId ||
            previewData.detailsOneLabel ||
            previewData.detailsOneValue ||
            previewData.isFieldHighlighted?.('details.one.id') ||
            previewData.isFieldHighlighted?.('details.one.label') ||
            previewData.isFieldHighlighted?.('details.one.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              className="box"
              pl={2}
              minWidth="40px"
              label={previewData.detailsOneLabel}
              id={previewData.detailsOneId}
              value={previewData.detailsOneValue}
              align="left"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('details.one.id') ||
                previewData.isFieldHighlighted?.('details.one.label') ||
                previewData.isFieldHighlighted?.('details.one.defaultValue')
              }
            />
          )}

          {/* DETAILS TWO */}
          {(previewData.detailsTwoId ||
            previewData.detailsTwoLabel ||
            previewData.detailsTwoValue ||
            previewData.isFieldHighlighted?.('details.two.id') ||
            previewData.isFieldHighlighted?.('details.two.label') ||
            previewData.isFieldHighlighted?.('details.two.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              className="box"
              pl={2}
              minWidth="40px"
              label={previewData.detailsTwoLabel}
              id={previewData.detailsTwoId}
              value={previewData.detailsTwoValue}
              align="left"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('details.two.id') ||
                previewData.isFieldHighlighted?.('details.two.label') ||
                previewData.isFieldHighlighted?.('details.two.defaultValue')
              }
            />
          )}

          {/* DETAILS THREE */}
          {(previewData.detailsThreeId ||
            previewData.detailsThreeLabel ||
            previewData.detailsThreeValue ||
            previewData.isFieldHighlighted?.('details.three.id') ||
            previewData.isFieldHighlighted?.('details.three.label') ||
            previewData.isFieldHighlighted?.('details.three.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              className="box"
              pl={2}
              minWidth="40px"
              label={previewData.detailsThreeLabel}
              id={previewData.detailsThreeId}
              value={previewData.detailsThreeValue}
              align="left"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('details.three.id') ||
                previewData.isFieldHighlighted?.('details.three.label') ||
                previewData.isFieldHighlighted?.('details.three.defaultValue')
              }
            />
          )}

          {/* DETAILS FOUR */}
          {(previewData.detailsFourId ||
            previewData.detailsFourLabel ||
            previewData.detailsFourValue ||
            previewData.isFieldHighlighted?.('details.four.id') ||
            previewData.isFieldHighlighted?.('details.four.label') ||
            previewData.isFieldHighlighted?.('details.four.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              className="box"
              display={labelDisplay}
              pl={2}
              minWidth="40px"
              label={previewData.detailsFourLabel}
              id={previewData.detailsFourId}
              value={previewData.detailsFourValue}
              align="left"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('details.four.id') ||
                previewData.isFieldHighlighted?.('details.four.label') ||
                previewData.isFieldHighlighted?.('details.four.defaultValue')
              }
            />
          )}
        </Flex>

        {/* EVENT TICKET 2ND ROW */}
        <Flex
          justifyContent="space-between"
          px="12px"
          pt="20px"
          display={
            previewData.passType === 'loyalty' ||
            previewData.passType === 'stampCard' ||
            previewData.passType === 'coupon'
              ? 'none'
              : 'flex'
          }
        >
          {/* DETAILS FOUR */}
          {(previewData.detailsFourId ||
            previewData.detailsFourLabel ||
            previewData.detailsFourValue ||
            previewData.isFieldHighlighted?.('details.four.id') ||
            previewData.isFieldHighlighted?.('details.four.label') ||
            previewData.isFieldHighlighted?.('details.four.defaultValue')) && (
            <ApplePassField
              hideIdPlaceholder={previewData.hideIdPlaceholder}
              className="box"
              pl={2}
              minWidth="40px"
              label={previewData.detailsFourLabel}
              id={previewData.detailsFourId}
              value={previewData.detailsFourValue}
              align="left"
              titleColor={previewData.titleFontColor}
              bodyColor={previewData.bodyFontColor}
              minFontSize={8}
              maxFontSize={14}
              columnHeight="30px"
              valueHeight="16px"
              isHighlighted={
                previewData.isFieldHighlighted?.('details.four.id') ||
                previewData.isFieldHighlighted?.('details.four.label') ||
                previewData.isFieldHighlighted?.('details.four.defaultValue')
              }
            />
          )}

          {/* DETAILS FIVE */}
          {previewData.passType !== 'loyalty' &&
            previewData.passType !== 'stampCard' && (
              <>
                {(previewData.detailsFiveId ||
                  previewData.detailsFiveLabel ||
                  previewData.detailsFiveValue ||
                  previewData.isFieldHighlighted?.('details.five.id') ||
                  previewData.isFieldHighlighted?.('details.five.label') ||
                  previewData.isFieldHighlighted?.(
                    'details.five.defaultValue'
                  )) && (
                  <ApplePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    className="box"
                    pl={2}
                    minWidth="40px"
                    label={previewData.detailsFiveLabel}
                    id={previewData.detailsFiveId}
                    value={previewData.detailsFiveValue}
                    align="left"
                    titleColor={previewData.titleFontColor}
                    bodyColor={previewData.bodyFontColor}
                    minFontSize={8}
                    maxFontSize={14}
                    columnHeight="30px"
                    valueHeight="16px"
                    isHighlighted={
                      previewData.isFieldHighlighted?.('details.five.id') ||
                      previewData.isFieldHighlighted?.('details.five.label') ||
                      previewData.isFieldHighlighted?.(
                        'details.five.defaultValue'
                      )
                    }
                  />
                )}

                {/* DETAILS SIX */}
                {(previewData.detailsSixId ||
                  previewData.detailsSixLabel ||
                  previewData.detailsSixValue ||
                  previewData.isFieldHighlighted?.('details.six.id') ||
                  previewData.isFieldHighlighted?.('details.six.label') ||
                  previewData.isFieldHighlighted?.(
                    'details.six.defaultValue'
                  )) && (
                  <ApplePassField
                    hideIdPlaceholder={previewData.hideIdPlaceholder}
                    className="box"
                    pl={2}
                    minWidth="40px"
                    label={previewData.detailsSixLabel}
                    id={previewData.detailsSixId}
                    value={previewData.detailsSixValue}
                    align="left"
                    titleColor={previewData.titleFontColor}
                    bodyColor={previewData.bodyFontColor}
                    minFontSize={8}
                    maxFontSize={14}
                    columnHeight="30px"
                    valueHeight="16px"
                    isHighlighted={
                      previewData.isFieldHighlighted?.('details.six.id') ||
                      previewData.isFieldHighlighted?.('details.six.label') ||
                      previewData.isFieldHighlighted?.(
                        'details.six.defaultValue'
                      )
                    }
                  />
                )}
              </>
            )}
        </Flex>

        {/* APP LINK */}
        {!!previewData.appleAppLink[0] && (
          <>
            {!templatesPage ? (
              <Box>
                {(previewData.appleIconLogo || previewData.iconLogo) && (
                  <Image
                    position="absolute"
                    bottom="6px"
                    left="6px"
                    h="24px"
                    w="24px"
                    src={previewData.appleIconLogo || previewData.iconLogo}
                    borderRadius="5px"
                    alt=""
                    boxShadow={
                      previewData.isFieldHighlighted?.('design.shared.icon') ||
                      previewData.isFieldHighlighted?.('design.apple.icon')
                        ? '0 0 0 2px #FF4935'
                        : 'none'
                    }
                  />
                )}
              </Box>
            ) : (
              <Box>
                {(previewData.appleIconLogo || previewData.iconLogo) && (
                  <Image
                    position="absolute"
                    bottom="10px"
                    left="10px"
                    h="26px"
                    w="26px"
                    src={previewData.appleIconLogo || previewData.iconLogo}
                    borderRadius="5px"
                    alt=""
                    boxShadow={
                      previewData.isFieldHighlighted?.('design.shared.icon') ||
                      previewData.isFieldHighlighted?.('design.apple.icon')
                        ? '0 0 0 2px #FF4935'
                        : 'none'
                    }
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* NFC ICON */}
        {((typeof previewData.nfc === 'object' && previewData.nfc?.enabled) ||
          previewData.nfc?.enabled === true) && (
          <CustomNfcIcon
            fontSize={isNfcFieldHighlighted ? '30px' : '20px'}
            pos="absolute"
            bottom="10px"
            right="6px"
            color={previewData.bodyFontColor}
            borderRadius="4px"
            border={isNfcFieldHighlighted ? '2px solid #FF4935' : 'none'}
            p={isNfcFieldHighlighted ? '4px' : '0'}
          />
        )}

        {/* BARCODE */}
        {/* {(createView || clientView || isEdit || createTemplate) && */}
        {createTemplate &&
          previewBarcodeImage &&
          !!previewData.barcode.enabled &&
          !!previewData.barcode.type &&
          !previewData.nfc.enabled && (
            <>
              <Flex w="full" justifyContent="center" my={3}>
                <Flex
                  direction="column"
                  bg="white"
                  mt={2}
                  w="fit-content"
                  borderRadius="10px"
                  justifyContent="center"
                  alignItems="center"
                  border={
                    isBarcodeFieldHighlighted ? '2px solid #FF4935' : 'none'
                  }
                  p={isBarcodeFieldHighlighted ? '4px' : '1'}
                >
                  <Box
                    mt={
                      previewData.barcode.displayText &&
                      previewData.barcode.value
                        ? 2
                        : 0
                    }
                  >
                    <BarcodePreview
                      type={previewData.barcode.type}
                      value={previewData.barcode.value}
                    />
                  </Box>
                  {previewData.barcode.displayText &&
                    (() => {
                      const displayValue =
                        previewData.barcode.value !== ''
                          ? previewData.barcode.value
                          : previewData.barcode.source === 'custom'
                            ? ''
                            : previewData.barcode.source?.toUpperCase();
                      if (!displayValue) return null;
                      return (
                        <Text
                          fontSize="11px"
                          textStyle="headingFamilyRegularApplePay"
                          mt={2}
                          borderRadius="4px"
                          border="none"
                          p="0"
                        >
                          {displayValue}
                        </Text>
                      );
                    })()}
                </Flex>
              </Flex>
            </>
          )}
      </Box>

      {/* BAR ICON */}
      {!templatesPage && (
        <Flex
          position="absolute"
          inset="90% 10% auto 0"
          my={3}
          width="full"
          justifyContent="center"
          direction="row"
          align="center"
        >
          <Divider
            orientation="vertical"
            borderColor="black"
            w="25%"
            borderWidth="2px"
            borderRadius="25px"
          />
        </Flex>
      )}
    </>
  );
};

export default ApplePassFront;
